<template>
  <div class="container mb-5">
    <div class="row mt-3 ms-1 me-1">
      <div class="col-lg-8">
        <header>
          <h3 class="mb-1">Utbetalinger</h3>
          <p class="ut-lead">
            Utbetalingene fra VIVA til bankkontoen din finner du i VIVA-kontoen din. VIVA gir bare deg tilgang til dem.
          </p>
        </header>

        <a :href="links.login" target="_blank" rel="noopener noreferrer" class="bo_btn text-decoration-none ut-login">
          <font-awesome-icon :icon="['fas', 'external-link-alt']" /> Logg inn hos VIVA
        </a>

        <section class="ut-card" aria-labelledby="ut-see">
          <h5 id="ut-see" class="ut-card__title">
            <font-awesome-icon :icon="['fas', 'university']" class="ut-card__icon" aria-hidden="true" />
            Se utbetalingene
          </h5>
          <ol class="ut-steps">
            <li>Logg inn hos VIVA.</li>
            <li>Gå til <strong>Kontoer</strong>.</li>
            <li>Velg <strong>Bankoverføringer</strong>. Der står hver utbetaling til bankkontoen din.</li>
          </ol>
          <p class="ut-note">
            Alle bevegelser på kontoen, også salg og gebyrer, ser du under <strong>Kontoer</strong>: velg kontoen, klikk
            <strong>Detaljer</strong> og velg periode. I Viva.com-appen på mobilen finner du det samme under
            <strong>Kontoer</strong>.
          </p>
          <a :href="links.transactions" target="_blank" rel="noopener noreferrer" class="ut-help">
            VIVAs veiledning om transaksjoner og bankoverføringer
            <font-awesome-icon :icon="['fas', 'external-link-alt']" aria-hidden="true" />
          </a>
        </section>

        <section class="ut-card" aria-labelledby="ut-auto">
          <h5 id="ut-auto" class="ut-card__title">
            <font-awesome-icon :icon="['fas', 'question-circle']" class="ut-card__icon" aria-hidden="true" />
            Kommer ikke pengene inn på bankkontoen?
          </h5>
          <p class="mb-2">
            Uten automatiske utbetalinger blir pengene stående på VIVA-kontoen din til du overfører dem selv. Slik slår
            du dem på:
          </p>
          <ol class="ut-steps">
            <li>Gå til <strong>Kontoer</strong>, velg kontoen og klikk <strong>Detaljer</strong>.</li>
            <li>Klikk <strong>Utbetalingsinnstillinger</strong> og slå på <strong>Aktiver utbetalinger</strong>.</li>
            <li>
              Velg bankkontoen din som mottaker, eller legg den til med <strong>+ Legg til mottaker</strong>. Velg hvor
              ofte det skal betales ut under <strong>Intervallvalg</strong>, og klikk <strong>Utfør</strong>.
            </li>
          </ol>
          <p class="ut-note">
            Under <strong>Neste utbetaling</strong> ser du når neste utbetaling kommer. Beløpet i
            <strong>Minimumsbeløp for utbetaling</strong> blir stående igjen på kontoen.
          </p>
          <div class="ut-tip">
            <font-awesome-icon :icon="['fas', 'lightbulb']" class="ut-tip__icon" aria-hidden="true" />
            <p class="mb-0">
              <strong>Utbetaling hver dag?</strong> Da anbefaler vi VIVA-planen <strong>Omni-channel Premium</strong>.
              Den har 30 gratis utgående overføringer. Planen din ser du hos VIVA.com under
              <strong>Innstillinger</strong> → <strong>Abonnementsplaner</strong>.
            </p>
          </div>
          <a :href="links.payoutSettings" target="_blank" rel="noopener noreferrer" class="ut-help">
            VIVAs veiledning om utbetalingsinnstillinger
            <font-awesome-icon :icon="['fas', 'external-link-alt']" aria-hidden="true" />
          </a>
        </section>

        <p class="ut-foot">
          Vil du se om én bestemt kortbetaling gikk gjennom? Det ser du under
          <router-link to="/vivaPaymentTransactions">Kortbetalinger</router-link>.
          Hva hver utbetaling består av, dag for dag med gebyrene, finner du og regnskapsføreren under
          <router-link to="/accountingVouchers">Regnskapsbilag</router-link>.
        </p>
      </div>
    </div>
  </div>
</template>

<script>

// VIVA's own pages, in Norwegian. The menu names on this page are VIVA's, from these help articles (2026-10-22).
const LINKS = {
  login: "https://www.vivapayments.com/nb/signin",
  transactions: "https://euhelp.viva.com/nb/articles/4866575-hvordan-kan-jeg-se-alle-transaksjonene-pa-bedriftskontoen-min",
  payoutSettings: "https://euhelp.viva.com/nb/articles/9459574-utbetalingsinnstillinger"
};

/**
 * «Utbetalinger»: where the owner finds VIVA's payouts to their bank account. For the merchant's ADMIN, and for
 * SUPER_ADMIN reading it to them on the phone. Laid out like the other report pages (X-rapport): the standard
 * container, left-aligned.
 *
 * The payouts cannot be shown here. VIVA's Account Transactions API and its webhook are merchant-level only, with
 * each merchant's own keys (VIVA, 2026-10-22), and holding a key to every merchant's VIVA account does not scale. So
 * the page sends the owner to VIVA, in VIVA's own words. It also covers the usual reason money does not reach the
 * bank: automatic payouts are off. See CagistaServices docs/viva-isv-connected-accounts.md.
 */
export default {
  name: "VivaPayoutReports",
  data() {
    return { links: LINKS };
  }
};
</script>

<style scoped>
.ut-lead { color: var(--bo-muted); margin: 0; }
.ut-login { display: inline-flex; align-items: center; gap: 8px; margin-top: 16px; padding: 8px 16px; }

.ut-card {
  margin-top: 18px; padding: 18px 20px; border: 1px solid var(--bo-line); border-radius: 12px;
  background: var(--bo-surface); color: var(--bo-ink);
}
.ut-card__title { display: flex; align-items: center; gap: 10px; font-size: 1.1rem; font-weight: 600; margin: 0 0 12px; }
.ut-card__icon { color: var(--bo-brand-ink); }
.ut-steps { margin: 0 0 12px; padding-left: 1.4rem; }
.ut-steps li { margin-bottom: 6px; }
.ut-note { color: var(--bo-ink-2); font-size: 0.92rem; margin-bottom: 10px; }
.ut-tip {
  display: flex; gap: 10px; align-items: flex-start; margin-bottom: 12px; padding: 10px 14px; border-radius: 10px;
  background: var(--bo-info-bg); color: var(--bo-info-fg); border: 1px solid var(--bo-info-line); font-size: 0.92rem;
}
.ut-tip__icon { margin-top: 3px; }
.ut-help { display: inline-flex; align-items: center; gap: 6px; font-size: 0.92rem; }
.ut-foot { margin-top: 18px; color: var(--bo-ink-2); }

/* A finger (touch.css): both ways out to VIVA reach the 48px floor. */
@media (pointer: coarse) {
  .ut-login, .ut-help { min-height: var(--bo-touch-target, 48px); }
}
</style>
